import { createAsyncThunk } from "@reduxjs/toolkit";
import apiConfig from "constants/apiConfig";
import { t } from "i18next";
import { fetchCampaignActivity, fetchCampaign as fetchCampaignAPI, fetchCampaigns, fetchCampaignsCount as fetchCampaignsCountAPI, markCampaignComplete, requestCampaignApproval } from "services/api/campaign";
import { AuthState } from "slices/auth/reducer";
import { ERROR } from "types";
import { CampaignActivity, CampaignDetail, CampaignsResponse, CampaignsSlice } from "types/campaign";
import { getErrorMessage, isFresh } from "utils/helpers";

const STALE_TIME = apiConfig.FETCH_CAMPAIGNS.staleTime;

export const fetchCampaignsList = createAsyncThunk<{campaigns: CampaignsResponse, page: number, status: string, brand_id: string}, {page: number, status: string, brand_id: string, force?: boolean}, {rejectValue: string}>(
    'campaigns/list',
    async ({ page, status, brand_id, force = false }, { getState, rejectWithValue }) => {
      
        try {
            const state = getState() as { campaigns: CampaignsSlice, auth: AuthState };
            const selectedAgencyId = state.auth.selectedAgencyId;
            if(!selectedAgencyId) throw new Error("AgencyNotSelected!");

            const STALE_TIME = apiConfig.FETCH_CAMPAIGNS.staleTime;

            if(page ===1 && !force && isFresh(state.campaigns.data?.[brand_id]?.[status]?.last_fetched_at ?? null, STALE_TIME)){
                return rejectWithValue(ERROR.CACHED);
            }

            const campaigns = await fetchCampaigns(selectedAgencyId, {page, status: status, ...(brand_id !== 'all' ? {brand_id} : {})});
            return { campaigns, page, status, brand_id };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch campaigns list!"));
        }
    }
);

export const fetchCampaign= createAsyncThunk<{campaignId: string, campaignData: CampaignDetail}, {campaignId: string, force?: boolean}, {rejectValue: string}>(
    'campaigns/campaign',
    async ({ campaignId, force = false }, { getState, rejectWithValue }) => {
        const state = getState() as { campaigns: CampaignsSlice, auth: AuthState };
        if (!force && isFresh(state.campaigns.items[campaignId]?.last_fetched_at ?? null, STALE_TIME) && !state.campaigns.items[campaignId]?.expired) {
            return rejectWithValue(ERROR.CACHED);
        }
        try {
            const campaignData = await fetchCampaignAPI(state.auth.selectedAgencyId!, campaignId);
            return { campaignId, campaignData };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch campaign with ID: "+campaignId));
        }
    }
);

export const markCampaignCompleted = createAsyncThunk<{campaignId: string}, {campaignId: string}, {rejectValue: string}>(
    'campaign/mark-completed',
    async ({ campaignId }, { getState, rejectWithValue }) => {
        try {
            const state =  getState() as { campaigns: CampaignsSlice, auth: AuthState };
            const agencyId = state.auth.selectedAgencyId;
            if(!agencyId) throw new Error("No agency selected!");

            await markCampaignComplete(agencyId, campaignId);

            return { campaignId };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to mark campaign as completed!"));
        }
    }
)

export const sendCampaignForApprovalRequest = createAsyncThunk<{campaignId: string}, {campaignId: string}, {rejectValue: string}>(
    'campaign/send-for-approval',
    async ({ campaignId }, { getState, rejectWithValue }) => {
        try {
            const state =  getState() as { campaigns: CampaignsSlice, auth: AuthState };
            const agencyId = state.auth.selectedAgencyId;
            if(!agencyId) throw new Error("No agency selected!");

            await requestCampaignApproval(agencyId, campaignId);

            return { campaignId };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to request approval!"));
        }
    }
)

export const fetchCampaignsCount = createAsyncThunk<{totals: any, brand_id: string}, {brand_id?: string, force?: boolean}, {rejectValue: string}>(
    'campaigns/total',
    async ({brand_id = 'all', force}, { getState, rejectWithValue }) => {
        try {
            const STALE_TIME = apiConfig.FETCH_CAMPAIGNS_COUNT.staleTime;
            const state =  getState() as { campaigns: CampaignsSlice, auth: AuthState };
            const agencyId = state.auth.selectedAgencyId;
            if(!agencyId) throw new Error("No agency selected!");
            
            if(!force && isFresh(state.campaigns.totals[brand_id]?.last_fetched_at ?? null, STALE_TIME)){
                return rejectWithValue(ERROR.CACHED)
            }

            const totals = await fetchCampaignsCountAPI(agencyId, brand_id);

            return {totals, brand_id}

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "failed to fetch campaign counts"))
        }
    }
) 

export const fetchCampaignActivityThunk = createAsyncThunk<{items: CampaignActivity[]}, {force?: boolean}, {rejectValue: string}>(
    'campaigns/activity',
    async ({force}, { getState, rejectWithValue }) => {
        try {
            const STALE_TIME = apiConfig.FETCH_CAMPAIGN_ACTIVITY.staleTime;
            const state =  getState() as { campaigns: CampaignsSlice, auth: AuthState };
            const agencyId = state.auth.selectedAgencyId;
            if(!agencyId) throw new Error("No agency selected!");
            
            if(!force && isFresh(state.campaigns.activity?.last_fetched_at ?? null, STALE_TIME)){
                return rejectWithValue(ERROR.CACHED)
            }

            const {items} = await fetchCampaignActivity(agencyId);

            return {items}

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "failed to fetch campaign counts"))
        }
    }
) 